import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../api/api';

const AirportTouristData = () => {
  const [nationalities, setNationalities] = useState([]);
  const [monthlyData, setMonthlyData] = useState({});
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [newNationality, setNewNationality] = useState('');

  const months = [
    { id: 1, name: 'OCAK', nameTr: 'Ocak' },
    { id: 2, name: 'ŞUBAT', nameTr: 'Şubat' },
    { id: 3, name: 'MART', nameTr: 'Mart' },
    { id: 4, name: 'NİSAN', nameTr: 'Nisan' },
    { id: 5, name: 'MAYIS', nameTr: 'Mayıs' },
    { id: 6, name: 'HAZİRAN', nameTr: 'Haziran' },
    { id: 7, name: 'TEMMUZ', nameTr: 'Temmuz' },
    { id: 8, name: 'AĞUSTOS', nameTr: 'Ağustos' },
    { id: 9, name: 'EYLÜL', nameTr: 'Eylül' },
    { id: 10, name: 'EKİM', nameTr: 'Ekim' },
    { id: 11, name: 'KASIM', nameTr: 'Kasım' },
    { id: 12, name: 'ARALIK', nameTr: 'Aralık' }
  ];

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  useEffect(() => {
    // Global yükleme sinyali gönder
    window.dispatchEvent(new CustomEvent('app_loading', { detail: loading }));
  }, [loading]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`/marketing-budget/airport-tourist-data/?year=${selectedYear}`);

      // Veriyi işle
      const processedNationalities = {};
      const processedMonthlyData = {};

      if (response && response.length > 0) {
        response.forEach(item => {
          const nationality = item.nationality || item.nationality_name || '';
          if (!processedNationalities[nationality]) {
            processedNationalities[nationality] = true;
          }

          const key = `${nationality}_${item.month}`;
          processedMonthlyData[key] = item.value || 0;
        });
      }

      setNationalities(Object.keys(processedNationalities).sort());
      setMonthlyData(processedMonthlyData);
    } catch (error) {
      // Silently handle error
      // Eğer endpoint yoksa boş liste döndür
      setNationalities([]);
      setMonthlyData({});
    } finally {
      setLoading(false);
    }
  };

  const handleAddNationality = () => {
    if (newNationality.trim() && !nationalities.includes(newNationality.trim())) {
      setNationalities([...nationalities, newNationality.trim()].sort());
      setNewNationality('');
    }
  };

  const handleValueChange = async (nationality, month, value) => {
    const key = `${nationality}_${month}`;
    setMonthlyData(prev => ({
      ...prev,
      [key]: value || 0
    }));

    // API'ye kaydet
    try {
      const numericValue = parseFloat(value) || 0;
      await apiRequest('/marketing-budget/airport-tourist-data/', {
        method: 'POST',
        body: JSON.stringify({
          nationality: nationality,
          year: selectedYear,
          month: month,
          value: numericValue
        })
      });
    } catch (error) {
      // Silently handle error
    }
  };

  const getValue = (nationality, month) => {
    const key = `${nationality}_${month}`;
    return monthlyData[key] || '';
  };

  const getTotal = (nationality) => {
    return months.reduce((sum, month) => {
      const key = `${nationality}_${month.id}`;
      return sum + (parseFloat(monthlyData[key]) || 0);
    }, 0);
  };

  const getGrandTotal = () => {
    return nationalities.reduce((sum, nationality) => sum + getTotal(nationality), 0);
  };

  const getPercentage = (nationality) => {
    const total = getTotal(nationality);
    const grandTotal = getGrandTotal();
    if (grandTotal === 0) return 0;
    return ((total / grandTotal) * 100).toFixed(2);
  };

  const getMonthTotal = (month) => {
    return nationalities.reduce((sum, nationality) => {
      const key = `${nationality}_${month.id}`;
      return sum + (parseFloat(monthlyData[key]) || 0);
    }, 0);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 sm:p-6 lg:p-8">
      <div className="max-w-full mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Airport Tourist Data
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Havalimanı turist verileri girişi ve görüntüleme
          </p>
        </div>

        {/* Year Selector */}
        <div className="mb-4 flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Yıl:
          </label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {/* Add Nationality */}
        <div className="mb-4 flex items-center gap-2">
          <input
            type="text"
            value={newNationality}
            onChange={(e) => setNewNationality(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddNationality()}
            placeholder="Yeni milliyet ekle..."
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex-1 max-w-xs"
          />
          <button
            onClick={handleAddNationality}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Ekle
          </button>
        </div>


        <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600">
                  MİLLİYETLER
                </th>
                {months.map(month => (
                  <th
                    key={month.id}
                    className="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600 min-w-[100px]"
                  >
                    {month.name}
                  </th>
                ))}
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-r border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-600">
                  TOPLAM
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-100 dark:bg-gray-600">
                  MİLLİYET PAYI (%)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {nationalities.map((nationality, index) => (
                <tr key={nationality} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700/50'}>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-600">
                    {nationality}
                  </td>
                  {months.map(month => (
                    <td key={month.id} className="px-2 py-2 border-r border-gray-200 dark:border-gray-600">
                      <input
                        type="number"
                        value={getValue(nationality, month.id)}
                        onChange={(e) => handleValueChange(nationality, month.id, e.target.value)}
                        placeholder="-"
                        min="0"
                        step="1"
                        className="w-full px-2 py-1 text-sm text-center border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </td>
                  ))}
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white text-center border-r border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-600">
                    {getTotal(nationality).toLocaleString('tr-TR')}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-white text-center bg-gray-100 dark:bg-gray-600">
                    {getPercentage(nationality)}%
                  </td>
                </tr>
              ))}
              {/* Total Row */}
              <tr className="bg-blue-50 dark:bg-blue-900/20 font-bold">
                <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-600">
                  TOPLAM
                </td>
                {months.map(month => (
                  <td key={month.id} className="px-2 py-2 text-sm font-bold text-gray-900 dark:text-white text-center border-r border-gray-200 dark:border-gray-600">
                    {getMonthTotal(month).toLocaleString('tr-TR')}
                  </td>
                ))}
                <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center border-r border-gray-200 dark:border-gray-600 bg-blue-100 dark:bg-blue-900/30">
                  {getGrandTotal().toLocaleString('tr-TR')}
                </td>
                <td className="px-4 py-3 text-sm font-bold text-gray-900 dark:text-white text-center bg-blue-100 dark:bg-blue-900/30">
                  100.00%
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AirportTouristData;








